
import './pwa';
import './i18n';
import './android';

import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import App from './App';
import { BUILD, LS_DARK_MODE, SENTRY_DSN } from './config';
import { applyPolyfills } from './polyfills';

console.log('Build', BUILD);

if (SENTRY_DSN) {
  Sentry.init({
    release: BUILD,
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.replayIntegration()
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Ignore errors thrown inside third-party embed scripts we cannot patch.
    // The YouTube iframe API (www-widgetapi.js) throws from its global `message`
    // listener when it receives unrelated cross-origin postMessages
    // (cf. Sentry REBORN-FRONT-176: "Cannot read properties of undefined (reading 'id')").
    denyUrls: [
      /www-widgetapi/i,
      /\/s\/player\//i,
    ],
  });
}

// Auto reload on asset fetching error
window.addEventListener('vite:preloadError', (event) => {
  console.log('[Error] Loading asset failed', event);
})

// Theme support
if (document.body) {
  const savedTheme = localStorage.getItem(LS_DARK_MODE);
  if (savedTheme === 'DARK') {
    document.body.className = 'dark';
  } else if (savedTheme === 'AUTO') {
    document.body.className = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } else {
    document.body.className = 'light';
  }
}

// Apply polyfill, then init the app
const promPolyfills = applyPolyfills();

// Set function ready
const appInit = async (dom) => {
  await promPolyfills;
  const root = createRoot(dom);
  root.render(<React.StrictMode>
    <App />
  </React.StrictMode>);
}

if (window?.reactDomTarget) {
  appInit(window.reactDomTarget);
} else {
  document.addEventListener("DOMContentLoaded", () => {
    appInit(window?.reactDomTarget);
  });
}
